<template>
  <div>
    <!-- Un emplacement existant a été retenu à la place de la nouvelle adresse. -->
    <v-alert v-if="value && value.locationId" border="left" colored-border color="primary" elevation="1" dense class="mb-3">
      <div class="d-flex align-center">
        <div>
          <div class="text-body-2 font-weight-medium">{{ $t('onboarding.existing-location.using', { name: value.locationName || '—' }) }}</div>
          <div class="text-caption text--secondary">{{ lineOf(value.address) }}</div>
        </div>
        <v-spacer />
        <v-btn text small color="primary" @click="reset">{{ $t('onboarding.existing-location.change') }}</v-btn>
      </div>
    </v-alert>

    <template v-else>
      <address-autocomplete :value="address" :label="label" :hint="hint" :required="required" @input="picked" />
      <div v-if="checking" class="text-caption text--secondary mt-n2 mb-2">
        <v-progress-circular indeterminate size="12" width="2" class="mr-1" />{{ $t('onboarding.existing-location.checking') }}
      </div>
      <div v-else-if="value && value.newLocation && value.newLocation.distinct" class="text-caption text--secondary mt-n2 mb-2">
        {{ $t('onboarding.existing-location.distinct-chosen') }}
      </div>
    </template>

    <!-- Un emplacement existe déjà à cette adresse : on le montre, et on laisse choisir. -->
    <v-dialog v-model="open" max-width="620" persistent scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-medium existing-location__title">
          <v-icon color="warning" small class="mr-2">mdi-map-marker-alert-outline</v-icon>
          <span>{{ $tc('onboarding.existing-location.title', matches.length) }}</span>
        </v-card-title>
        <v-card-text class="pt-1">
          <p class="text-body-2 mb-3">{{ $t('onboarding.existing-location.explained', { address: lineOf(address) }) }}</p>

          <v-card v-for="location in matches" :key="location.id" outlined class="pa-3 mb-3">
            <div class="d-flex align-start">
              <div>
                <div class="text-body-2 font-weight-medium">{{ location.name || $t('onboarding.existing-location.home-elsewhere') }}</div>
                <div class="text-caption">{{ lineOf(location.address) }}</div>
                <div class="text-caption text--secondary">
                  <v-icon x-small class="mr-1">mdi-domain</v-icon>{{ location.organizationName || '—' }}
                  <span v-if="location.home"> · {{ $t('onboarding.existing-location.home') }}</span>
                </div>
              </div>
              <v-spacer />
              <v-chip v-if="!location.sameOrganization" x-small outlined class="ml-2">{{ $t('onboarding.existing-location.other-organization') }}</v-chip>
            </div>

            <!-- Ce qui y est installé : de quoi reconnaître le lieu sans ouvrir sa fiche. -->
            <div class="mt-2 text-body-2">
              <template v-if="location.chargingPoints">
                <div v-if="!location.chargingPoints.length && !(location.meterDevices || []).length" class="text-caption text--secondary">
                  {{ $t('onboarding.existing-location.empty') }}
                </div>
                <div v-for="cp in location.chargingPoints" :key="cp.id" class="text-caption">
                  <v-icon x-small class="mr-1">mdi-ev-station</v-icon>{{ cp.identifier }}<span v-if="cp.name && cp.name !== cp.identifier"> — {{ cp.name }}</span>
                </div>
                <div v-for="md in location.meterDevices || []" :key="md.id" class="text-caption">
                  <v-icon x-small class="mr-1">mdi-meter-electric-outline</v-icon>{{ md.name }}
                </div>
              </template>
              <div v-else class="text-caption text--secondary">
                {{ $t('onboarding.existing-location.counts', { chargingPoints: location.chargingPointCount || 0, meterDevices: location.meterDeviceCount || 0 }) }}
              </div>
            </div>

            <div class="mt-2">
              <v-btn v-if="location.sameOrganization" small depressed color="primary" class="existing-location__button" @click="useExisting(location)">{{ $t('onboarding.existing-location.use') }}</v-btn>
              <span v-else class="text-caption text--secondary">{{ $t('onboarding.existing-location.not-usable') }}</span>
            </div>
          </v-card>
        </v-card-text>
        <v-divider />
        <!-- Les deux autres issues, à taille de texte : l'action attendue est « Utiliser cet emplacement ». -->
        <v-card-actions class="existing-location__actions">
          <v-btn small text class="existing-location__button" @click="reset">{{ $t('onboarding.existing-location.other-address') }}</v-btn>
          <v-spacer />
          <v-btn small text color="primary" class="existing-location__button" @click="createAnyway">{{ $t('onboarding.existing-location.create-anyway') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import AddressAutocomplete from './AddressAutocomplete.vue'
import CommissioningMixin from './CommissioningMixin'

/**
 * Une nouvelle adresse d'emplacement, qui ne crée pas un doublon sans le dire : si un emplacement
 * existe déjà à cette adresse (ici ou dans une autre organisation), on le montre — son organisation,
 * ce qui y est installé — et l'on choisit : l'utiliser, ou créer quand même un nouvel emplacement.
 *
 * La valeur : { locationId, locationName, address } quand on reprend l'existant,
 * { newLocation } quand on en crée un (avec `distinct` si c'est malgré un existant).
 */
export default {
  name: 'NewLocationPicker',

  components: { AddressAutocomplete },

  mixins: [CommissioningMixin],

  props: {
    value: { type: Object, default: null },
    organizationId: { type: String, required: true },
    label: { type: String, required: true },
    hint: { type: String, default: '' },
    required: { type: Boolean, default: false },
  },

  data() {
    return { matches: [], open: false, checking: false }
  },

  computed: {
    address() {
      return this.value?.newLocation || null
    },
  },

  methods: {
    async picked(address) {
      if (!address) {
        this.$emit('input', null)
        return
      }
      this.$emit('input', { newLocation: address })
      this.checking = true
      try {
        const result = await CommissioningRepository.locationsAtAddress(this.organizationId, this.asQuery({ address }))
        this.matches = result.items || []
        this.open = this.matches.length > 0
      } catch (e) {
        // La vérification est un confort : sans elle, l'adresse reste retenue telle quelle.
        this.matches = []
      } finally {
        this.checking = false
      }
    },

    useExisting(location) {
      this.open = false
      this.$emit('input', { locationId: location.id, locationName: location.name, address: location.address })
    },

    createAnyway() {
      this.open = false
      this.$emit('input', { newLocation: { ...this.address, distinct: true } })
    },

    reset() {
      this.open = false
      this.matches = []
      this.$emit('input', null)
    },

    lineOf(address) {
      return address ? this.formatAddress(address) : ''
    },
  },
}
</script>

<style scoped>
.existing-location__title {
  flex-wrap: nowrap;
  word-break: normal;
  line-height: 1.4;
}
/* Les boutons de l'application sont grands par défaut : ici, ce sont des choix, pas des appels à l'action. */
.existing-location__button {
  font-size: 0.8125rem !important;
  height: 30px !important;
}
.existing-location__actions {
  flex-wrap: wrap;
  gap: 4px;
}
</style>
